import React from 'react';
import { ethers } from "ethers";
import axios from 'axios';
import './App.css';
import Validation from './Validation.jsx';
import DISPERSE_ABI from './DisperseNft.abi.json';
import ERC1155_ABI from './ERC1155.abi.json';
import BOOST_NFT_ABI from './BoostNFT.abi.json';

// Optional: provide bytecode via env to enable in-browser deployment
// Add REACT_APP_BOOSTNFT_BYTECODE=0x... to your .env to deploy from this UI
const BOOST_NFT_BYTECODE = process.env.REACT_APP_BOOSTNFT_BYTECODE || '';

const SUPPORTED_CHAINIDS = [80094];
const NETWORK_NAMES = {80094: "Berachain Mainnet"};
const DISPERSE_CONTRACT_ADDR = {
  80094: "0x969E3F2C19D6Aef23D8b3914d24c7F74EEfD9F8B",
}
const DISPERSE_CONTRACT_LINK = {
  80094: <a href="https://beratrail.io/address/0x969E3F2C19D6Aef23D8b3914d24c7F74EEfD9F8B">0x969E3F2C19D6Aef23D8b3914d24c7F74EEfD9F8B</a>,
}

const BOOST_NFT_CONTRACT_ADDR = "0x5F5F745BeA0BCB464Fee648FEc319eA8d0082F47";

// Revert decoding helper for common OZ errors
const ERROR_IFACE = new ethers.utils.Interface([
  // Ownable/Admin
  'error OwnableUnauthorizedAccount(address)',
  // AccessControl
  'error AccessControlUnauthorizedAccount(address,bytes32)',
  'error AccessControlBadConfirmation()'
]);

function formatRevert(simErr, extra) {
  try {
    const data = simErr?.error?.data || simErr?.data || '';
    if (data && typeof data === 'string' && data.startsWith('0x')) {
      try {
        const parsed = ERROR_IFACE.parseError(data);
        return `${parsed.name}(${parsed.args?.map((x) => x.toString()).join(', ')})${extra ? ' — ' + extra : ''}`;
      } catch (_) {
        // fallthrough
      }
      return `Reverted with data: ${data.slice(0, 66)}…${extra ? ' — ' + extra : ''}`;
    }
    const reason = simErr?.reason || simErr?.message || 'Transaction would revert';
    return `${reason}${extra ? ' — ' + extra : ''}`;
  } catch {
    return `Transaction would revert${extra ? ' — ' + extra : ''}`;
  }
}

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // wallet state
      ethereum: null,
      provider: null,
      signer: null,
      connectedAccounts: [],
      walletStatus: 'no-wallet-detected',
      chainId: '',
      
      // tab state
      activeTab: 'mint',
      
      // minting state
  mintContract: '',
      mintName: '',
      mintDescription: '',
      mintSupply: '',
      mintImage: null,
      mintImagePreview: null,
      mintStatus: '',
      mintTxHash: '',
      mintError: '',
      lastMintedTokenId: null,
      
      // distribution state
      contract: BOOST_NFT_CONTRACT_ADDR,
      recipients: '',
      parsedRecipients: [],
  ids: '["1"]',
  parsedIds: ['1'],
      quantities: '',
      parsedQuantities: [],
      quantityToggle: 'quantityToggleAuto',
      contractError: '',
      recipientsError: 'empty',
  idsError: '',
      quantitiesError: 'empty',
      txnHash: '',
      failureReason: '',

  // deploy state
  newContractName: '',
  deployStatus: '',
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleImageChange = this.handleImageChange.bind(this);
    this.handleMint = this.handleMint.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.connectWallet = this.connectWallet.bind(this);
    this.switchNetwork = this.switchNetwork.bind(this);
    this.switchTab = this.switchTab.bind(this);
  this.deployNewContract = this.deployNewContract.bind(this);
  this.handleRecipientsCsvUpload = this.handleRecipientsCsvUpload.bind(this);
  }

  async componentDidMount() {
    await this.setWalletState();
  }

  async deployNewContract() {
    this.setState({ deployStatus: 'Deploying...' });
    try {
      if (!this.state.signer) {
        this.setState({ deployStatus: 'Connect your wallet first' });
        return;
      }

      if (!BOOST_NFT_BYTECODE || !ethers.utils.isHexString(BOOST_NFT_BYTECODE)) {
        this.setState({ deployStatus: 'Bytecode not configured. Set REACT_APP_BOOSTNFT_BYTECODE in .env to deploy from web.' });
        return;
      }

      // Note: newContractName is captured for future constructors; current ABI shows no constructor args
      const factory = new ethers.ContractFactory(BOOST_NFT_ABI, BOOST_NFT_BYTECODE, this.state.signer);
      const contract = await factory.deploy();
      this.setState({ deployStatus: 'Transaction sent. Waiting for confirmation...' });
      await contract.deployed();

      this.setState({
        deployStatus: `Deployed: ${contract.address}`,
        contract: contract.address
      });
    } catch (error) {
      console.error('Deploy error:', error);
      const friendly = formatRevert(error);
      this.setState({ deployStatus: `Failed: ${friendly}` });
    }
  }

  async setWalletState() {
    console.log("setWalletState");

    const { ethereum } = window;
    let provider = null, signer = null, walletStatus = "wallet-not-connected";
    if (ethereum) {
      provider = new ethers.providers.Web3Provider(ethereum);
      signer = provider.getSigner();
      const network = await provider.getNetwork();

      const connectedAccounts = await provider.listAccounts();
      console.log("connected accounts: " + JSON.stringify(connectedAccounts));

      if (connectedAccounts.length === 0) {
        console.debug("Setting wallet state: wallet-not-connected");
        walletStatus = "wallet-not-connected";
      } else if (!SUPPORTED_CHAINIDS.includes(network.chainId)) {
        console.debug("Setting wallet state: wrong-network");
        walletStatus = "wrong-network";
      } else {
        console.debug("Setting wallet state: empty (everything good)");
        walletStatus = "";
      }

      this.setState({
        ethereum: ethereum,
        provider: provider,
        signer: signer,
        chainId: network.chainId,
        connectedAccounts: connectedAccounts,
        walletStatus: walletStatus
      });

      ethereum.on("chainChanged", this.setWalletState.bind(this));
      ethereum.on("accountsChanged", this.setWalletState.bind(this));
    }
  }

  async connectWallet() {
    await this.state.ethereum.request({
      method: 'eth_requestAccounts'
    });
    const accts = await this.state.provider.listAccounts();
    if (accts.length > 0) {
      await this.setWalletState();
    } else {
      throw Error("Connected to ethereum but provider.listAccounts() returned empty!");
    }
  }

  async switchNetwork(event) {
    let desiredChainId;
    switch (event.target.id) {
      case "button-berachain":
        desiredChainId = 80094;
        break;
      default:
        throw new Error("unexpected switch fallthrough for button id " + event.target.id);
    }

    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: "0x" + desiredChainId.toString(16) }],
    });

    this.setWalletState();
  }

  switchTab(tab) {
    this.setState({ activeTab: tab });
  }

  handleImageChange(event) {
    const file = event.target.files[0];
    if (file) {
      this.setState({ mintImage: file });
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.setState({ mintImagePreview: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  }

  async handleMint() {
  const { mintName, mintDescription, mintSupply, mintImage, mintContract } = this.state;
    
  if (!mintContract || !mintName || !mintDescription || !mintSupply || !mintImage) {
      this.setState({ mintError: 'Please fill all fields and select an image' });
      return;
    }

    if (!process.env.REACT_APP_PINATA_API_KEY || !process.env.REACT_APP_PINATA_SECRET) {
      this.setState({ mintError: 'Pinata API keys not configured. Please add REACT_APP_PINATA_API_KEY and REACT_APP_PINATA_SECRET to your .env file' });
      return;
    }

    try {
      this.setState({ mintStatus: 'uploading-image', mintError: '' });

      // Upload image
      const formData = new FormData();
      formData.append('file', mintImage);
      const imageRes = await axios.post('https://api.pinata.cloud/pinning/pinFileToIPFS', formData, {
        headers: {
          'pinata_api_key': process.env.REACT_APP_PINATA_API_KEY,
          'pinata_secret_api_key': process.env.REACT_APP_PINATA_SECRET,
          'Content-Type': 'multipart/form-data'
        }
      });

      this.setState({ mintStatus: 'uploading-metadata' });

      // Upload metadata
      const metadata = {
        name: mintName,
        description: mintDescription,
        image: `ipfs://${imageRes.data.IpfsHash}`,
        attributes: [{ trait_type: "Type", value: "Community Boost NFT" }]
      };

      const metadataRes = await axios.post('https://api.pinata.cloud/pinning/pinJSONToIPFS', metadata, {
        headers: {
          'pinata_api_key': process.env.REACT_APP_PINATA_API_KEY,
          'pinata_secret_api_key': process.env.REACT_APP_PINATA_SECRET,
          'Content-Type': 'application/json'
        }
      });
      const tokenURI = `ipfs://${metadataRes.data.IpfsHash}`;
      console.log('Metadata uploaded:', tokenURI);

      this.setState({ mintStatus: 'minting' });

  // Mint the NFT to the selected contract
  const boostContract = new ethers.Contract(mintContract, BOOST_NFT_ABI, this.state.signer);

      // Preflight: try a static call to surface clear errors (e.g., lack of MINTER/owner role)
      try {
        await boostContract.callStatic.mint(
          this.state.connectedAccounts[0],
          mintSupply,
          tokenURI,
          mintName
        );
      } catch (simErr) {
        console.error('Mint simulation failed:', simErr);
        const friendly = formatRevert(simErr, 'Common causes: not authorized to mint, invalid supply, or contract-specific rules.');
        this.setState({ mintStatus: 'mint-error', mintError: friendly });
        return;
      }

      const tx = await boostContract.mint(
        this.state.connectedAccounts[0], // mint to your wallet
        mintSupply,
        tokenURI,
        mintName
      );

      this.setState({ mintStatus: 'minting-in-progress', mintTxHash: tx.hash });

      const receipt = await tx.wait();
      console.log('Mint transaction success:', receipt);

      // Parse the minted token ID from logs
      const mintEvent = receipt.events?.find(e => e.event === 'TransferSingle');
      const tokenId = mintEvent?.args?.id?.toString();

      this.setState({ 
        mintStatus: 'mint-success',
        lastMintedTokenId: tokenId,
        mintName: '',
        mintDescription: '',
        mintSupply: '',
        mintImage: null,
        mintImagePreview: null
      });

    } catch (error) {
      console.error('Mint error:', error);
      this.setState({ 
        mintStatus: 'mint-error',
  mintError: error.message || 'Minting failed'
      });
    }
  }

  calcQtyFromIds(parsedIds) {
    return parsedIds.map(x => "1");
  }

  validateContract(contract) {
    if (!contract) {
      this.setState({contractError: "empty"});
    } else if (!contract.match(/^0x[a-fA-F0-9]{40}$/)) {
      this.setState({contractError: "Unable to parse contract address"});
    } else {
      this.setState({contractError: ""});
    }
  }

  validateRecipients(recipients) {
    this.setState({parsedRecipients: []});
    if (!recipients || recipients.trim() === '') {
      this.setState({recipientsError: "empty"});
      return;
    }

    try {
      const parsedRecipients = this.parseRecipientsText(recipients);
      if (parsedRecipients.length === 0) {
        this.setState({recipientsError: "No addresses found."});
        return;
      }
      const validAddresses = parsedRecipients.every(x => x.match(/^0x[a-fA-F0-9]{40}$/));
      if (!validAddresses) {
        this.setState({recipientsError: "Unable to parse recipient address."});
      } else {
        this.setState({recipientsError: ""});
        this.setState({parsedRecipients});
      }
    } catch (e) {
      this.setState({recipientsError: "Unable to parse recipients. Use JSON array, one per line, or comma-separated."});
    }
  }

  // Accepts JSON array, one per line, or comma-separated addresses. Returns an array of addresses.
  parseRecipientsText(text) {
    if (!text) return [];

  const clean = (s) => (s || '').trim().replace(/^"|"$/g, '').replace(/^'|'$/g, '');

    // Try JSON first
    try {
      const maybe = JSON.parse(text.replace(/'/g, '"'));
      if (Array.isArray(maybe)) {
        return maybe.map(clean).filter(Boolean);
      }
    } catch (_) {
      // fall through
    }

    // If contains newlines, split by lines
    if (/[\r\n]/.test(text)) {
      return text
        .split(/[\r\n]+/)
        .map(clean)
        .filter(Boolean);
    }

    // If contains commas, split by comma
    if (text.includes(',')) {
      return text
        .split(',')
        .map(clean)
        .filter(Boolean);
    }

    // Otherwise, try to extract any addresses found in the string
    const matches = text.match(/0x[a-fA-F0-9]{40}/g) || [];
    return matches.map(clean);
  }

  // Handle CSV uploads. Extract any 0x... addresses from the file and populate recipients.
  handleRecipientsCsvUpload(event) {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = String(e.target.result || '');
        // Grab all 0x addresses across the CSV
        const addrs = (content.match(/0x[a-fA-F0-9]{40}/g) || []).map((x) => x.trim());
        const unique = Array.from(new Set(addrs));
        const asTextArea = unique.join('\n');
        this.setState({ recipients: asTextArea }, () => this.validateRecipients(asTextArea));
      } catch (err) {
        console.error('CSV parse error:', err);
        this.setState({ recipientsError: 'Unable to parse CSV file.' });
      }
    };
    reader.onerror = () => {
      this.setState({ recipientsError: 'Failed to read CSV file.' });
    };
    reader.readAsText(file);
  }

  validateIds(ids) {
    this.setState({parsedIds: []});
    if (!ids) {
      this.setState({idsError: "empty"});
    } else {
      try {
        const parsedIds = JSON.parse(ids.replace(/'/g,'"'));

        if (!parsedIds.every(x => typeof x === "string")) {
          this.setState({idsError: "Unable to parse ID as string."});
        } else {
          this.setState({idsError: ""});
          this.setState({parsedIds: parsedIds});

          if (this.state.quantityToggle === "quantityToggleAuto") {
            this.setState({parsedQuantities: this.calcQtyFromIds(parsedIds)});
          } else {
            this.validateQuantities(parsedIds, this.state.quantities);
          }
        }
      } catch {
        this.setState({idsError: "Unable to parse IDs array."});
      }
    }
  }

  validateQuantities(ids, qtys) {
    if (this.state.quantityToggle === "quantityToggleAuto") {
      this.setState({quantitiesError: ""});
    } else if (!qtys) {
      this.setState({parsedQuantities: []});
      this.setState({quantitiesError: "empty"});
    } else {
      this.setState({parsedQuantities: []});
      try {
        const parsedQuantities = JSON.parse(qtys.replace(/'/g,'"'));

        if (!parsedQuantities.every(x => typeof x === "string")) {
          this.setState({quantitiesError: "Unable to parse quantity as string."});
        } else if (parsedQuantities.length !== ids.length) {
          this.setState({quantitiesError: `Invalid length (found ${ids.length} IDs, but ${parsedQuantities.length} quantities)`});
        } else {
          this.setState({quantitiesError: ""});
          this.setState({parsedQuantities: parsedQuantities});
        }
      } catch {
        this.setState({quantitiesError: "Unable to parse quantities array."});
      }
    }
  }

  handleChange(event) {
    this.setState({[event.target.name]: event.target.value});

    switch (event.target.name) {
      case "contract":
        this.validateContract(event.target.value);
        break;
      case "recipients":
        this.validateRecipients(event.target.value);
        break;
      case "ids":
        this.validateIds(event.target.value);
        break;
      case "quantities":
        this.validateQuantities(this.state.parsedIds, event.target.value);
        break;
      case "quantityToggle":
        if (event.target.value === "quantityToggleAuto") {
          this.setState({parsedQuantities: this.calcQtyFromIds(this.state.parsedIds)});
        }
        break;
      default:
        console.warn("unexpected switch fallthrough for event " + event.target.name);
    }
  }

  async handleSubmit() {
    console.log("Dispersing tokens");

    let nftContractAddr, recipients, ids, quantities;
    try {
      nftContractAddr = this.state.contract;
  // Use already-parsed recipients to support multiple input formats
  recipients = this.state.parsedRecipients;
      ids = JSON.parse(this.state.ids);
      if (this.state.quantityToggle === "quantityToggleAuto")
        quantities = this.state.parsedQuantities;
      else
        quantities = JSON.parse(this.state.quantities);
    } catch (err) {
      console.error(err);
      this.setState({
        failureReason: "Unable to parse parameters",
        walletStatus: "transaction-fail"
      });
      return;
    }

    console.log(`Ready to commit transaction. Parameters:
  contract: \t${JSON.stringify(nftContractAddr)},
  recipients: \t${JSON.stringify(recipients)},
  token ids: \t${JSON.stringify(ids)},
  quantities: \t${JSON.stringify(quantities)},
  binary data: \t"0x"
    `);

    let nftContract, balances;
    try {
      nftContract = await new ethers.Contract(this.state.contract, ERC1155_ABI, this.state.signer);
      const ownerAddresses = Array(ids.length).fill(this.state.connectedAccounts[0]);
      balances = await nftContract.balanceOfBatch(ownerAddresses, ids);
      console.log("balances: ", balances);

    } catch (err) {
      console.error(err);
      this.setState({
        failureReason: "Unable to determine contract balance. Did you provide a valid ERC-1155 NFT contract address?",
        walletStatus: "transaction-fail"
      });
      return;
    }

    for (var i = 0; i < ids.length; i++) {
      try {
        const want = ethers.BigNumber.from(quantities[i]);
        const have = ethers.BigNumber.from(balances[i]);
        if (want.gt(have)) {
          const err = `Insufficient balance for token ID ${ids[i]}. You are trying to send ${want.toString()}, but have ${have.toString()}.`;
          console.error(err);
          this.setState({
            failureReason: err,
            walletStatus: "transaction-fail"
          });
          return;
        }
      } catch (cmpErr) {
        console.error('Balance comparison failed:', cmpErr);
        this.setState({
          failureReason: 'Balance check failed. Ensure IDs and quantities are valid integers.',
          walletStatus: 'transaction-fail'
        });
        return;
      }
    }

    const disperseContractAddr = DISPERSE_CONTRACT_ADDR[this.state.chainId];
    if (!disperseContractAddr) {
      const err = "No contract address specified for chainId " + this.state.chainId;
      console.error(err);
      this.setState({
        failureReason: err,
        walletStatus: "transaction-fail"
      });
      return;
    }

    this.setState({walletStatus: "approve-pending"});

    let isApproved;
    try {
      isApproved = await nftContract.isApprovedForAll(this.state.connectedAccounts[0], disperseContractAddr);
    } catch (err) {
      console.error(err);
      this.setState({
        failureReason: "Unable to determine contract approval. Did you provide a valid ERC-1155 NFT contract address?",
        walletStatus: "transaction-fail"
      });
      return;
    }

    console.log("existing approval status: " + isApproved);

    if (!isApproved) {
      try {
        const tx = await nftContract.setApprovalForAll(disperseContractAddr, true);
        this.setState({
          txnHash: tx.hash,
          walletStatus: "approve-in-progress"
        });

        const receipt = await tx.wait();
        console.log("Transaction success. receipt: ", receipt);
      } catch (err) {
        console.error(err);
        this.setState({
          failureReason: JSON.stringify(err, null, 2),
          walletStatus: "transaction-fail"
        });
        return;
      }
    }

  this.setState({walletStatus: "disperse-pending"});

    const disperseContract = await new ethers.Contract(disperseContractAddr, DISPERSE_ABI, this.state.signer);
    try {
      // Preflight simulate to catch reverts early and show clearer error
      try {
        await disperseContract.callStatic.disperse(
          nftContractAddr,
          recipients,
          ids,
          quantities,
          "0x"
        );
      } catch (simErr) {
        console.error('Disperse simulation failed:', simErr);
        const friendly = formatRevert(simErr, 'Check approval, balances, and array lengths.');
        this.setState({ failureReason: friendly, walletStatus: 'transaction-fail' });
        return;
      }

      const tx = await disperseContract.disperse(
        nftContractAddr,
        recipients,
        ids,
        quantities,
        "0x"
      );

      this.setState({
        txnHash: tx.hash,
        walletStatus: "disperse-in-progress"
      });

      const receipt = await tx.wait();
      console.log("Transaction success. receipt: ", receipt);
      this.setState({walletStatus: "disperse-success"});
    } catch (err) {
      console.error(err);
      this.setState({
        failureReason: JSON.stringify(err, null, 2),
        walletStatus: "transaction-fail"
      });
    }
  }

  render() {
    const networkName = NETWORK_NAMES[this.state.chainId] ? NETWORK_NAMES[this.state.chainId] : "unsupported network";
    const connectionStatusMessage = (this.state.connectedAccounts.length > 0 && this.state.provider) ? <p>Wallet connected to {networkName}.</p> : null;

    // Mint section status messages
    const mintStatusMessage = () => {
      switch (this.state.mintStatus) {
        case 'uploading-image':
          return <p>📤 Uploading image to IPFS...</p>;
        case 'uploading-metadata':
          return <p>📝 Uploading metadata to IPFS...</p>;
        case 'minting':
          return <p>⏳ Sign transaction to mint NFT...</p>;
        case 'minting-in-progress':
          return <p>🔄 Minting in progress. Tx hash: {this.state.mintTxHash}</p>;
        case 'mint-success':
          return <div>
            <p>✅ NFT minted successfully!</p>
            <p>Token ID: {this.state.lastMintedTokenId}</p>
            <p>Tx hash: {this.state.mintTxHash}</p>
          </div>;
        case 'mint-error':
          return <p style={{color: 'red'}}>❌ {this.state.mintError}</p>;
        default:
          return null;
      }
    };

  // Distribution section validation
  let contractPreview, recipientsPreview, quantitiesPreview;

    if (this.state.contractError) {
      contractPreview = <i>{this.state.contractError}</i>;
    } else {
      contractPreview = this.state.contract;
    }

    if (this.state.recipientsError) {
      recipientsPreview = <i>{this.state.recipientsError}</i>;
    } else {
      if (this.state.parsedRecipients.length === 1) {
        recipientsPreview = "1 recipient";
      } else {
        recipientsPreview = `${this.state.parsedRecipients.length} recipients`;
      }
    }

    if (this.state.quantityToggle === 'quantityToggleAuto') {
      quantitiesPreview = "1 of each ID, for each recipient";
    } else {
      if (this.state.quantitiesError) {
        quantitiesPreview = <i>{this.state.quantitiesError}</i>;
      } else {
        if (this.state.parsedQuantities.length === 1) {
          quantitiesPreview = "1 quantity";
        } else {
          quantitiesPreview = `${this.state.parsedQuantities.length} quantities`;
        }
      }
    }

    const buttonDisabled = this.state.contractError
      || this.state.recipientsError
      || this.state.idsError
      || (this.state.quantitiesError && this.state.quantityToggle === 'quantityToggleManual');

    let quantityValue = this.state.quantities;
    let quantityDisabled = false;
    if (this.state.quantityToggle === "quantityToggleAuto") {
      quantityValue = JSON.stringify(this.state.parsedQuantities);
      quantityDisabled = true;
    }

    return <div className="App">
      <header className="App-header">
        <h1>Bull ISH Dispenser</h1>
        <h2>Mint community boost NFTs and distribute them to holders.</h2>
        <p>
          Create NFTs for different communities (Pudgy Penguins, Azuki, etc.) and airdrop them to snapshot holders.
        </p>
        <p>
          <span>Boost NFT Contract: <a href={`https://beratrail.io/address/${BOOST_NFT_CONTRACT_ADDR}`}>{BOOST_NFT_CONTRACT_ADDR}</a></span><br />
          <span className={SUPPORTED_CHAINIDS.includes(this.state.chainId) ? "" : "hidden"}>Disperse Contract: {DISPERSE_CONTRACT_LINK[this.state.chainId]}</span>
        </p>
      </header>

      {/* Wallet Connection States */}
      <main className={this.state.walletStatus === "no-wallet-detected" ? "" : "hidden"}>
        <h3>No wallet detected. Install MetaMask: <a href="https://metamask.io/">metamask.io</a></h3>
      </main>

      <main className={this.state.walletStatus === "wallet-not-connected" ? "" : "hidden"}>
        <h3>Connect your wallet to continue: <button id="connect" type="button" onClick={this.connectWallet}>Connect Wallet</button></h3>
      </main>

      <main className={this.state.walletStatus === "wrong-network" ? "" : "hidden"}>
        <h3>Switch to Berachain to continue: </h3>
        <ul>
          <li><h3><button className="switchNetwork" id="button-berachain" type="button" onClick={this.switchNetwork}>Berachain</button></h3></li>
        </ul>
      </main>

      {/* Transaction States */}
      <main className={this.state.walletStatus === "approve-pending" ? "" : "hidden"}>
        <h3>Sign transaction to approve NFT dispersal...</h3>
      </main>

      <main className={this.state.walletStatus === "approve-in-progress" ? "" : "hidden"}>
        <h3>Approval transaction in progress. Tx hash: {this.state.txnHash}</h3>
      </main>

      <main className={this.state.walletStatus === "disperse-pending" ? "" : "hidden"}>
        <h3>Sign transaction to disperse NFT...</h3>
      </main>

      <main className={this.state.walletStatus === "disperse-in-progress" ? "" : "hidden"}>
        <h3>Disperse transaction in progress. Tx hash: {this.state.txnHash}</h3>
      </main>

      <main className={this.state.walletStatus === "disperse-success" ? "" : "hidden"}>
        <h3>NFT dispersal complete! Tx hash: {this.state.txnHash}</h3>
        <h3><button id="reset" type="button" onClick={() => this.setState({walletStatus: ""})}>Continue</button></h3>
      </main>

      <main className={this.state.walletStatus === "transaction-fail" ? "" : "hidden"}>
        <h3>Transaction failed!</h3>
        <p><code>{this.state.failureReason}</code></p>
        <h3><button id="reset" type="button" onClick={() => this.setState({walletStatus: ""})}>Reset</button></h3>
      </main>

      {/* Main Interface */}
      <main className={this.state.walletStatus === "" ? "" : "hidden"}>
        {connectionStatusMessage}
        
        {/* Tab Navigation */}
        <div className="tab-nav" style={{marginBottom: '40px', textAlign: 'center'}}>
          <button 
            className={this.state.activeTab === 'mint' ? 'tab active' : 'tab'}
            onClick={() => this.switchTab('mint')}
            style={{marginRight: '10px', padding: '12px 24px', fontSize: '1.2rem'}}
          >
            🎨 Mint NFTs
          </button>
          <button 
            className={this.state.activeTab === 'distribute' ? 'tab active' : 'tab'}
            onClick={() => this.switchTab('distribute')}
            style={{padding: '12px 24px', fontSize: '1.2rem'}}
          >
            📤 Distribute NFTs
          </button>
        </div>

        {/* Mint Tab */}
        <div className={this.state.activeTab === 'mint' ? "" : "hidden"}>
          <h3>Create New Boost NFT</h3>

          {/* Deploy New Boost Contract */}
          <div style={{marginBottom: '20px', padding: '20px', border: '1px solid #ccc'}}>
            <h4>Deploy New Boost Contract</h4>
            <input
              type="text"
              placeholder="Contract name (e.g. PudgyBoost)"
              value={this.state.newContractName || ''}
              onChange={(e) => this.setState({ newContractName: e.target.value })}
            />
            <button onClick={this.deployNewContract} style={{ marginLeft: '10px' }}>Deploy Contract</button>
            {this.state.deployStatus && <p>{this.state.deployStatus}</p>}
          </div>
          
          <form autoComplete="off">
            <label htmlFor="mintContract">Contract Address</label>
            <div className="input-wrapper">
              <input 
                type="text" 
                name="mintContract" 
                value={this.state.mintContract} 
                onChange={this.handleChange} 
                placeholder="0x..."
              />
            </div>
            <label htmlFor="mintName">NFT Name <i>(e.g., "Pudgy Penguin Boost")</i></label>
            <div className="input-wrapper">
              <input 
                type="text" 
                name="mintName" 
                value={this.state.mintName} 
                onChange={this.handleChange} 
                placeholder="Pudgy Penguin Boost"
                autoComplete="off"
              />
            </div>

            <label htmlFor="mintDescription">Description</label>
            <div className="input-wrapper">
              <input 
                type="text" 
                name="mintDescription" 
                value={this.state.mintDescription} 
                onChange={this.handleChange} 
                placeholder="Boost NFT for Pudgy Penguin holders"
                autoComplete="off"
              />
            </div>

            <label htmlFor="mintSupply">Supply <i>(how many to mint)</i></label>
            <div className="input-wrapper">
              <input 
                type="number" 
                name="mintSupply" 
                value={this.state.mintSupply} 
                onChange={this.handleChange} 
                placeholder="1000"
                autoComplete="off"
              />
            </div>

            <label htmlFor="mintImage">Image</label>
            <div className="input-wrapper">
              <input 
                type="file" 
                name="mintImage" 
                onChange={this.handleImageChange}
                accept="image/*"
              />
            </div>

            {this.state.mintImagePreview && (
              <div style={{textAlign: 'center', margin: '20px 0'}}>
                <img 
                  src={this.state.mintImagePreview} 
                  alt="Preview" 
                  style={{maxWidth: '200px', maxHeight: '200px', border: '1px solid #ccc'}}
                />
              </div>
            )}
          </form>

          {mintStatusMessage()}

          <div className="button-container">
            <button 
              id="mint" 
              type="button" 
              disabled={!this.state.mintName || !this.state.mintDescription || !this.state.mintSupply || !this.state.mintImage || this.state.mintStatus.includes('progress') || this.state.mintStatus.includes('pending') || this.state.mintStatus.includes('uploading')}
              onClick={this.handleMint}
              style={{padding: '12px 24px', fontSize: '1.2rem', marginTop: '12px'}}
            >
              🎨 Mint NFT
            </button>
          </div>
        </div>

        {/* Distribute Tab */}
        <div className={this.state.activeTab === 'distribute' ? "" : "hidden"}>
          <h3>Distribute Boost NFTs</h3>
          
          <form autoComplete="off">
            <label htmlFor="contract">NFT contract <i>(Boost NFT contract address)</i></label>
            <div className="input-wrapper">
              <input type="text" name="contract" value={this.state.contract} autoComplete="off" onChange={this.handleChange} maxLength="42" />
            </div>

            <label htmlFor="recipients">Recipients <i>(JSON array, one per line, or comma-separated; or upload CSV)</i></label>
            <div className="input-wrapper">
              <textarea name="recipients" value={this.state.recipients} autoComplete="off" onChange={this.handleChange} rows="4" placeholder={'One per line, comma-separated, or JSON array.\nExample (JSON): ["0x123...", "0xabc..."]'} />
            </div>
            <div className="input-wrapper" style={{ marginTop: '8px' }}>
              <input type="file" accept=".csv,.txt" onChange={this.handleRecipientsCsvUpload} />
              <span style={{ marginLeft: '8px' }}>Upload CSV to extract 0x addresses</span>
            </div>

            <label htmlFor="quantities">Quantities</label>
            <div className="input-radio-wrapper">
              <div className="input-radio-wrapper-item">
                <input type="radio" id="quantityToggleAuto" name="quantityToggle" value="quantityToggleAuto" onChange={this.handleChange} checked={this.state.quantityToggle === "quantityToggleAuto"} />
                <label htmlFor="quantityToggleAuto">Automatic selection (1 of each ID, for each recipient)</label>
              </div>
              <div className="input-radio-wrapper-item">
                <input type="radio" id="quantityToggleManual" name="quantityToggle" value="quantityToggleManual" onChange={this.handleChange} checked={this.state.quantityToggle === "quantityToggleManual"} />
                <label htmlFor="quantityToggleManual">Manual quantities</label>
              </div>
            </div>

            <div className="input-wrapper quantity-input">
              <input type="text" name="quantities" value={quantityValue} disabled={quantityDisabled} autoComplete="off" onChange={this.handleChange} placeholder='["1"]' />
            </div>

          </form>

          <p>Contract: {contractPreview} <Validation valid={!this.state.contractError} /></p>
          <p>Recipients: {recipientsPreview} <Validation valid={!this.state.recipientsError} /></p>
          <p>Quantities: {quantitiesPreview} <Validation valid={this.state.quantityToggle === "quantityToggleAuto" || !this.state.quantitiesError} /></p>

          <div className="button-container">
            <button 
              id="disperse" 
              type="button" 
              disabled={buttonDisabled} 
              onClick={this.handleSubmit}
              style={{padding: '12px 24px', fontSize: '1.2rem', marginTop: '12px'}}
            >
              📤 Distribute NFTs
            </button>
          </div>
        </div>

      </main>
    </div>;
  }
}

export default App;